import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../../core/permissons/CheckPermissons';
import { showError, showSuccessAlert } from '../../../../../core/alert/sweetAlertConfig';
import GateManagementLayout from '../GateManagementLayout';
import { CameraModal, DeleteCameraModal, ViewCameraModal } from './CameraModals';

interface Camera {
    id: number;
    name: string;
    deviceUUID: string;
    macAddress?: string;
    ipAddress?: string;
    deviceID?: string;
    channelID?: number;
    role: 'ENTRY' | 'EXIT';
    status: string;
}

const Cameras: React.FC = () => {
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
    
    const [formData, setFormData] = useState({
        name: '',
        deviceUUID: '',
        macAddress: '',
        ipAddress: '',
        deviceID: '',
        channelID: '',
        role: '',
        status: 'ACTIVE'
    });

    const canManage = checkPermission('ROLE_MANAGE_CAMERA');

    useEffect(() => {
        fetchCameras();
    }, []);

    const fetchCameras = async () => {
        setLoading(true);
        try {
            const response = await apiDefinitions.getCameras(0, 50, {});
            if (response.data.status === 'S1000') {
                setCameras(response.data.data || []);
            } else {
                showError(response.data.statusDescription || 'Failed to fetch cameras');
            }
        } catch (error: any) {
            showError('Error fetching cameras: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openAddModal = () => {
        setIsEdit(false);
        setFormData({
            name: '',
            deviceUUID: '',
            macAddress: '',
            ipAddress: '',
            deviceID: '',
            channelID: '',
            role: '',
            status: 'ACTIVE'
        });
        setShowModal(true);
    };

    const openEditModal = (camera: Camera) => {
        setIsEdit(true);
        setSelectedCamera(camera);
        setFormData({
            name: camera.name,
            deviceUUID: camera.deviceUUID,
            macAddress: camera.macAddress || '',
            ipAddress: camera.ipAddress || '',
            deviceID: camera.deviceID || '',
            channelID: camera.channelID?.toString() || '',
            role: camera.role,
            status: camera.status
        });
        setShowModal(true);
    };

    const openDeleteModal = (camera: Camera) => {
        setSelectedCamera(camera);
        setShowDeleteModal(true);
    };

    const openViewModal = (camera: Camera) => {
        setSelectedCamera(camera);
        setShowViewModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                channelID: formData.channelID ? parseInt(formData.channelID) : null
            };

            if (isEdit && selectedCamera) {
                const response = await apiDefinitions.updateCamera({ ...payload, id: selectedCamera.id });
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Camera updated successfully');
                    setShowModal(false);
                    fetchCameras();
                } else {
                    showError(response.data.statusDescription || 'Failed to update camera');
                }
            } else {
                const response = await apiDefinitions.createCamera(payload);
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Camera created successfully');
                    setShowModal(false);
                    fetchCameras();
                } else {
                    showError(response.data.statusDescription || 'Failed to create camera');
                }
            }
        } catch (error: any) {
            showError('Error saving camera: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedCamera) return;
        setLoading(true);
        try {
            const response = await apiDefinitions.deleteCamera(selectedCamera.id.toString());
            if (response.data.status === 'S1000') {
                showSuccessAlert('Camera deleted successfully');
                setShowDeleteModal(false);
                fetchCameras();
            } else {
                showError(response.data.statusDescription || 'Failed to delete camera');
            }
        } catch (error: any) {
            showError('Error deleting camera: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const filteredCameras = cameras.filter(c => 
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (c.deviceUUID || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <GateManagementLayout activeTab="cameras">
            <div className="card mt-2">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">ANPR Camera List</h5>
                    {canManage && (
                        <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                            <i className="ti ti-plus me-1"></i> Add Camera
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control w-25"
                            placeholder="Search cameras..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive">
                        <Table className="table table-borderless table-striped">
                            <thead className="table-light">
                                <tr>
                                    <th>Name</th>
                                    <th>UUID / IP</th>
                                    <th>Role</th>
                                    <th>Channel</th>
                                    <th>Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && filteredCameras.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center">Loading...</td></tr>
                                ) : filteredCameras.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center">No cameras found</td></tr>
                                ) : (
                                    filteredCameras.map(camera => (
                                        <tr key={camera.id}>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="fw-medium text-dark">{camera.name}</span>
                                                    <small className="text-muted">{camera.deviceID || 'No Hardware ID'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <code>{camera.deviceUUID}</code>
                                                    <small className="text-info">{camera.ipAddress || 'No IP'}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge bg-${camera.role === 'ENTRY' ? 'primary' : 'warning'}-subtle text-${camera.role === 'ENTRY' ? 'primary' : 'warning'}`}>
                                                    {camera.role}
                                                </span>
                                            </td>
                                            <td>{camera.channelID ?? 'N/A'}</td>
                                            <td>
                                                <span className={`badge bg-${camera.status === 'ACTIVE' ? 'success' : 'danger'}-subtle text-${camera.status === 'ACTIVE' ? 'success' : 'danger'}`}>
                                                    {camera.status}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <div className="hstack gap-2 justify-content-end">
                                                    <button 
                                                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill" 
                                                        title="View"
                                                        onClick={() => openViewModal(camera)}
                                                    >
                                                        <i className="feather-eye" />
                                                    </button>
                                                    {canManage && (
                                                        <>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-primary rounded-pill" 
                                                                title="Edit"
                                                                onClick={() => openEditModal(camera)}
                                                            >
                                                                <i className="feather-edit" />
                                                            </button>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-danger rounded-pill" 
                                                                title="Delete"
                                                                onClick={() => openDeleteModal(camera)}
                                                            >
                                                                <i className="feather-trash-2" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <CameraModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={handleSubmit}
                formData={formData}
                handleInputChange={handleInputChange}
                loading={loading}
                isEdit={isEdit}
            />

            <DeleteCameraModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                cameraName={selectedCamera?.name || ''}
                loading={loading}
            />

            <ViewCameraModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                camera={selectedCamera}
            />
        </GateManagementLayout>
    );
};

export default Cameras;
